import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Banknote, Check, Eye, Scissors } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DateInput from "@/components/DateInput";
import PeriodSelect from "@/components/PeriodSelect";
import ProjectSelect from "@/components/ProjectSelect";
import FirmSelect from "@/components/FirmSelect";
import PaymentDetailDialog from "@/components/PaymentDetailDialog";
import ExcelButton from "@/components/ExcelButton";
import HakedisLinesEditor from "@/components/HakedisLinesEditor";
import DeductionDialog from "@/components/DeductionDialog";
import { fmtTL, fmtDate } from "@/lib/utils";

const EMPTY = { project_id: "", firm_id: "", firm_name: "", payment_no: 1, period_start: "", period_end: "", gross_amount: 0, deductions: 0, net_amount: 0, status: "hazirlandi", notes: "" };
const STATUSES = [{v: "hazirlandi", l: "Hazırlandı"}, {v: "onayda", l: "Onayda"}, {v: "onaylandi", l: "Onaylandı"}, {v: "odendi", l: "Ödendi"}];

export default function ProgressPayments() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [firms, setFirms] = useState([]);
  const [firmFilter, setFirmFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detail, setDetail] = useState(null);
  const [dedFor, setDedFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [lines, setLines] = useState([]);
  const [lineRefs, setLineRefs] = useState([]);
  const [years, setYears] = useState([]);
  const [periodKey, setPeriodKey] = useState(0);
  const thisYear = new Date().getFullYear();
  const formYears = Array.from(new Set([...years, thisYear, thisYear - 1])).sort((a, b) => b - a);

  const load = () => {
    const params = {};
    if (filter) params.project_id = filter;
    if (firmFilter) params.firm_id = firmFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/progress-payments", { params }).then(r => setRows(r.data));
  };
  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/firms").then(r => setFirms(r.data));
    api.get("/analytics/summary").then(r => {
      const a = Number((r.data.date_from || "").slice(0, 4)), b = Number((r.data.date_to || "").slice(0, 4));
      if (a && b) setYears(Array.from({ length: b - a + 1 }, (_, i) => b - i));
    });
  }, []);
  useEffect(() => { load(); }, [filter, firmFilter, dateFrom, dateTo]);
  const totalGross = rows.reduce((s, r) => s + (r.gross_amount || 0), 0);
  const totalDed = rows.reduce((s, r) => s + (r.deductions || 0), 0);

  const filled = lines.filter(l => l.description && Number(l.qty) > 0);
  const linesGross = filled.reduce((s, l) => s + Number(l.qty) * (Number(l.unit_price) || 0), 0);
  const highCount = lines.filter((l, i) => l.description && Number(l.qty) > 0 && lineRefs[i]?.high).length;

  const save = async () => {
    if (!form.project_id) { toast.error("Proje zorunlu"); return; }
    if (filled.length && !form.period_end) { toast.error("Kalemli hakedişte dönem bitiş tarihi zorunlu"); return; }
    if (highCount && !window.confirm(`${highCount} kalemde birim fiyat geçmiş projelerin ortalamasından yüksek. Yine de kaydedilsin mi?`)) return;
    const gross = filled.length ? linesGross : Number(form.gross_amount) || 0;
    const ded = 0;
    const items = filled.map(l => ({ poz_no: l.poz_no, description: l.description, unit: l.unit, qty: Number(l.qty), unit_price: Number(l.unit_price) || 0 }));
    if (saving) return;
    setSaving(true);
    let created;
    try {
      created = await api.post("/progress-payments", { ...form, firm_id: form.firm_id || null, payment_no: Number(form.payment_no), gross_amount: gross, deductions: ded, net_amount: gross - ded, items });
    } catch (e) { toast.error("Kaydedilemedi: " + (e.response?.data?.detail || e.message)); return; }
    finally { setSaving(false); }
    toast.success("Hakediş oluşturuldu — kesintileri seçebilirsiniz"); setOpen(false); setForm(EMPTY); setLines([]); load();
    setDedFor(created.data);
  };
  const remove = async (id) => {
    if (!window.confirm("Hakedişi silmek istiyor musunuz?")) return;
    try { await api.delete(`/progress-payments/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const nextStatus = async (id, status) => { await api.put(`/progress-payments/${id}/status?status=${status}`); toast.success("Durum güncellendi"); load(); };

  const statusColor = { hazirlandi: "bg-slate-500/15 text-slate-700 dark:text-slate-300", onayda: "bg-amber-500/15 text-amber-700 dark:text-amber-400", onaylandi: "bg-blue-500/15 text-blue-700 dark:text-blue-400", odendi: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Hakediş ve Ödemeler" subtitle="Dönemsel hakediş düzenleme, onay ve ödeme takibi.">
        <ExcelButton params={{ project_id: filter, firm_id: firmFilter, date_from: dateFrom, date_to: dateTo }} testid="payments-excel-button" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-payment-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni Hakediş</Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Yeni Hakediş</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" /></div>
              <div className="col-span-2"><Label>Firma</Label><FirmSelect firms={firms} value={form.firm_id} onChange={v => setForm({...form, firm_id: v, firm_name: firms.find(f => f.id === v)?.name || ""})} testid="form-firm" allLabel="Firma seçilmedi (opsiyonel)" /></div>
              <div><Label>Hakediş No</Label><Input type="number" data-testid="input-payment-no" value={form.payment_no} onChange={e => setForm({...form, payment_no: e.target.value})} /></div>
              <div><Label>Durum</Label><Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label>Hakediş Dönemi</Label>
                <div className="mt-1"><PeriodSelect years={formYears} allowAll={false} testid="form-period"
                  onRange={(a, b) => setForm(f => ({ ...f, period_start: a, period_end: b }))} /></div>
                <p className="text-[11px] text-muted-foreground mt-1">Ay veya çeyrek seçince başlangıç ve bitiş tarihleri otomatik dolar; isterseniz aşağıdan değiştirebilirsiniz.</p>
              </div>
              <div><Label>Dönem Başlangıç</Label><DateInput data-testid="form-period-start" value={form.period_start} onChange={e => setForm({...form, period_start: e.target.value})} /></div>
              <div><Label>Dönem Bitiş</Label><DateInput data-testid="form-period-end" value={form.period_end} onChange={e => setForm({...form, period_end: e.target.value})} /></div>
              <div className="col-span-2 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label>Hakediş Kalemleri</Label>
                  {highCount > 0 && <span className="text-xs font-semibold text-rose-600 dark:text-rose-400" data-testid="high-price-warning">{highCount} kalemde birim fiyat geçmiş ortalamanın üzerinde</span>}
                </div>
                <HakedisLinesEditor lines={lines} onChange={setLines} projectId={form.project_id} onRefs={setLineRefs} />
              </div>
              <div><Label>Brüt Tutar (TL)</Label><Input type="number" step="0.01" data-testid="input-gross" disabled={filled.length > 0}
                value={filled.length ? linesGross.toFixed(2) : form.gross_amount} onChange={e => setForm({...form, gross_amount: e.target.value})} /></div>
              <div className="text-xs text-muted-foreground self-end pb-2">Kesintiler kayıttan sonra açılan pencereden seçilir.</div>
              <div className="col-span-2"><Label>Notlar</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              <div className="col-span-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-between">
                <span className="text-sm font-semibold">Brüt Tutar (kesintiler hariç)</span>
                <span className="font-mono-nums text-xl font-bold text-amber-700 dark:text-amber-400">{fmtTL(filled.length ? linesGross : Number(form.gross_amount) || 0)}</span>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} disabled={saving} data-testid="save-payment-button">{saving ? "Kaydediliyor..." : "Kaydet"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="rounded-xl border border-border bg-card p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="payments-filter-bar">
        <FilterField label="Proje"><ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" /></FilterField>
        <FilterField label="Firma"><FirmSelect firms={firms} value={firmFilter} onChange={setFirmFilter} testid="filter-firm" /></FilterField>
        <FilterField label="Dönem"><PeriodSelect key={periodKey} years={years} onRange={(a, b) => { setDateFrom(a); setDateTo(b); }} testid="filter-period" /></FilterField>
        <FilterField label="Başlangıç"><DateInput className="w-40" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriodKey(k => k + 1); }} data-testid="filter-date-from" /></FilterField>
        <FilterField label="Bitiş"><DateInput className="w-40" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriodKey(k => k + 1); }} data-testid="filter-date-to" /></FilterField>
        {(filter || firmFilter || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilter(""); setFirmFilter(""); setDateFrom(""); setDateTo(""); setPeriodKey(k => k + 1); }} data-testid="clear-filters">Filtreleri temizle</Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <Banknote className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz hakediş yok</div>
        </div>
      ) : (
        <>
        <div className="flex flex-wrap gap-6 mb-4 text-sm" data-testid="payments-summary">
          <span><span className="text-muted-foreground">Hakediş sayısı:</span> <strong className="font-mono-nums">{rows.length}</strong></span>
          <span><span className="text-muted-foreground">Toplam tutar:</span> <strong className="font-mono-nums text-amber-700 dark:text-amber-400">{fmtTL(totalGross)}</strong></span>
          <span><span className="text-muted-foreground">Kesinti:</span> <strong className="font-mono-nums text-rose-600">{fmtTL(totalDed)}</strong></span>
          <span><span className="text-muted-foreground">Net:</span> <strong className="font-mono-nums">{fmtTL(totalGross - totalDed)}</strong></span>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>No</TableHead><TableHead>Firma</TableHead><TableHead>Projeler</TableHead><TableHead>Dönem</TableHead><TableHead className="text-right">Brüt</TableHead><TableHead className="text-right">Kesinti</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">Kümülatif</TableHead><TableHead>Durum</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`payment-row-${r.id}`}>
                  <TableCell className="font-mono-nums font-bold">#{r.payment_no}</TableCell>
                  <TableCell className="font-medium text-sm max-w-[220px]">{r.firm_name || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px]">{(r.project_names?.length ? r.project_names : [projects.find(p => p.id === r.project_id)?.name]).filter(Boolean).join(", ")}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(r.period_start)} — {fmtDate(r.period_end)}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtTL(r.gross_amount)}</TableCell>
                  <TableCell className="text-right font-mono-nums text-rose-600 dark:text-rose-400" data-testid={`payment-ded-${r.id}`}>{r.deductions ? `-${fmtTL(r.deductions)}` : "—"}</TableCell>
                  <TableCell className="text-right font-mono-nums font-bold text-amber-700 dark:text-amber-400" data-testid={`payment-net-${r.id}`}>{fmtTL(r.net_amount)}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtTL(r.cumulative_amount)}</TableCell>
                  <TableCell><Badge className={statusColor[r.status]}>{STATUSES.find(s => s.v === r.status)?.l || r.status}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" title="Kesintiler" onClick={() => setDedFor(r)} data-testid={`deductions-${r.id}`}><Scissors className="w-4 h-4 text-rose-500" /></Button>
                    {r.line_count > 0 && <Button variant="ghost" size="icon" onClick={() => setDetail(r)} data-testid={`detail-${r.id}`}><Eye className="w-4 h-4" /></Button>}
                    {r.status !== "odendi" && (
                      <Button variant="ghost" size="icon" onClick={() => {
                        const next = r.status === "hazirlandi" ? "onayda" : r.status === "onayda" ? "onaylandi" : "odendi";
                        nextStatus(r.id, next);
                      }} data-testid={`advance-${r.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}
      <PaymentDetailDialog payment={detail} onClose={() => setDetail(null)} />
      {dedFor && <DeductionDialog payment={dedFor} onClose={() => setDedFor(null)} onSaved={load} />}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}
